import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDate, exportCSV, inputCls, labelCls } from "@/lib/constants";
import { Download, Printer } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const REPORTS = [
  ["disbursement", "Disbursement"],
  ["payout", "Payout"],
  ["contribution", "Contribution", "contribution"],
  ["dsa-performance", "DSA Performance"],
  ["product-sales", "Product-wise Sales"],
  ["bank-conversion", "Bank-wise Conversion"],
  ["daily-login", "Daily Login"],
  ["lead-journey", "Lead Journey"],
  ["custom-fields", "Field Reporting"],
  ["payout-statement", "Payout Statement"],
];

export default function Reports() {
  const { user } = useAuth();
  const canViewContribution = user?.role === "admin" || user?.role === "finance" || user?.role === "operations_manager";
  const visibleReports = REPORTS.filter(([, , gate]) => gate !== "contribution" || canViewContribution);
  const [active, setActive] = useState(visibleReports[0]?.[0] || "disbursement");
  const [data, setData] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  // field reporting
  const [cfProduct, setCfProduct] = useState("");
  const [cfKey, setCfKey] = useState("");
  const [cfValue, setCfValue] = useState("");
  const [cfRows, setCfRows] = useState([]);
  const [derivedFields, setDerivedFields] = useState([]);
  // statement
  const [stmtPartner, setStmtPartner] = useState("");
  const [stmt, setStmt] = useState(null);
  const [stmtMonth, setStmtMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [stmtAll, setStmtAll] = useState(false);

  const special = active === "custom-fields" || active === "payout-statement" || active === "lead-journey";
  const [journey, setJourney] = useState({ stages: [], rows: [] });

  useEffect(() => {
    if (active === "lead-journey") {
      api.get("/reports/lead-journey").then((r) => setJourney(r.data)).catch(() => setJourney({ stages: [], rows: [] }));
    }
  }, [active]);

  useEffect(() => {
    if (!special) api.get(`/reports/${active}`).then((r) => setData(r.data)).catch(() => setData([]));
  }, [active, special]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  const runFieldReport = useCallback(() => {
    if (!cfKey) { setCfRows([]); return; }
    api.get("/leads", { params: { cf_key: cfKey, cf_value: cfValue || undefined, loan_type: cfProduct || undefined } })
      .then((r) => setCfRows(r.data)).catch(() => setCfRows([]));
  }, [cfKey, cfValue, cfProduct]);

  useEffect(() => { runFieldReport(); }, [runFieldReport]);

  const loadStatement = useCallback(() => {
    if (!stmtPartner) { setStmt(null); return; }
    api.get("/statements/payout", { params: { partner_id: stmtPartner, month: stmtAll ? "" : stmtMonth } })
      .then((r) => setStmt(r.data)).catch(() => setStmt(null));
  }, [stmtPartner, stmtMonth, stmtAll]);

  useEffect(() => { loadStatement(); }, [loadStatement]);

  useEffect(() => {
    const prod = products.find((p) => p.name === cfProduct);
    if (cfProduct && prod && !(prod.fields && prod.fields.length)) {
      api.get("/leads", { params: { loan_type: cfProduct } }).then((r) => {
        const keys = new Set();
        r.data.forEach((l) => Object.keys(l.custom_fields || {}).forEach((k) => keys.add(k)));
        setDerivedFields([...keys].map((k) => ({ key: k, label: k.replace(/_/g, " ") })));
      }).catch(() => setDerivedFields([]));
    } else setDerivedFields([]);
  }, [cfProduct, products]);

  const activeFields = (products.find((p) => p.name === cfProduct)?.fields) || [];
  const fieldOptions = activeFields.length ? activeFields : derivedFields;

  const cols = {
    "daily-login": [["date", "Date", formatDate], ["count", "Logins", (v) => v]],
    "disbursement": [["customer_name", "Customer"], ["loan_type", "Product"], ["lender_name", "Lender"], ["partner_name", "Partner"], ["disbursed_amount", "Amount", formatINR], ["disbursed_at", "Date", formatDate]],
    "payout": [["customer_name", "Customer"], ["partner_name", "Partner"], ["lender_name", "Lender"], ["net_payout", "Net Payout", formatINR], ["tds_amount", "TDS", formatINR], ["status", "Status"]],
    "contribution": [["customer_name", "Customer"], ["loan_type", "Product"], ["lender_name", "Lender"], ["partner_name", "Partner"], ["disbursed_amount", "Disbursed", formatINR], ["lender_payout", "Lender Payout", formatINR], ["dsa_commission", "DSA Commission", formatINR], ["contribution", "Contribution", formatINR], ["margin_pct", "Margin %", (v) => `${v}%`]],
    "dsa-performance": [["partner_name", "DSA / Partner"], ["leads", "Leads"], ["disbursed", "Disbursed"], ["conversion", "Conversion %", (v) => `${v}%`], ["disbursed_amount", "Value", formatINR], ["payout", "Payout", formatINR]],
    "product-sales": [["loan_type", "Product"], ["total", "Total Leads"], ["disbursed", "Disbursed"], ["amount", "Disbursed Value", formatINR]],
    "bank-conversion": [["lender_name", "Lender"], ["type", "Type"], ["total", "Leads"], ["disbursed", "Disbursed"], ["conversion", "Conversion %", (v) => `${v}%`], ["amount", "Value", formatINR]],
  };
  const c = cols[active];

  const doExport = () => {
    if (active === "custom-fields") exportCSV(cfRows.map((l) => ({ customer: l.customer_name, phone: l.customer_phone, product: l.loan_type, [cfKey]: l.custom_fields?.[cfKey] ?? "", status: l.status })), "field-report.csv");
    else if (active === "payout-statement") exportCSV((stmt?.rows || []).map((r) => ({ date: r.date, customer: r.customer_name, loan_type: r.loan_type, lender: r.lender_name, disbursed: r.disbursed_amount, net_payout: r.net_payout, paid: r.status === "settled" ? (r.paid_amount ?? r.net_payout) : "", status: r.status, settled_on: r.settled_at ? formatDate(r.settled_at) : "", note: r.settle_note || "" })), `payout-statement${stmt?.month ? "-" + stmt.month : ""}.csv`);
    else if (active === "lead-journey") {
      const flat = (journey.rows || []).map((r) => {
        const row = {
          lead_no: r.lead_no, customer_name: r.customer_name, customer_phone: r.customer_phone, customer_email: r.customer_email,
          loan_type: r.loan_type, loan_amount: r.loan_amount, pan: r.pan, aadhaar: r.aadhaar, city: r.city, pincode: r.pincode,
          lender_name: r.lender_name, bank_application_no: r.bank_application_no, partner_name: r.partner_name,
          sales_manager_id: r.sales_manager_id, sales_manager_name: r.sales_manager_name,
          ops_manager_name: r.ops_manager_name, final_pdd_ops_manager: r.final_pdd_ops_manager,
          current_status: r.current_status,
          current_stage_entered_at: r.current_stage_entered_at ? formatDate(r.current_stage_entered_at) : "",
          pending_days_in_current_stage: r.pending_days_in_current_stage ?? "",
          created_at: r.created_at ? formatDate(r.created_at) : "",
          sanction_amount: r.sanction_amount, disbursed_amount: r.disbursed_amount, disbursed_at: r.disbursed_at ? formatDate(r.disbursed_at) : "",
          irr: r.irr, tenure: r.tenure, total_days_since_created: r.total_days_since_created ?? "",
        };
        (journey.stages || []).forEach((s) => { row[`entered_${s}`] = r.stage_entered?.[s] ? formatDate(r.stage_entered[s]) : ""; row[`days_in_${s}`] = r.stage_days?.[s] ?? ""; });
        return row;
      });
      exportCSV(flat, "lead-journey.csv");
    }
    else exportCSV(data, `${active}-report.csv`);
  };

  const totals = stmt?.totals || { total_net: 0, paid: 0, pending: 0, count: 0, settled_count: 0 };
  const stmtPeriod = stmt ? (stmt.month ? new Date(stmt.month + "-01T00:00:00").toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "All time") : "";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Reports &amp; Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Operational and performance insights</p>
        </div>
        <div className="flex gap-2">
          {active === "payout-statement" && stmt && <button data-testid="print-statement-button" onClick={() => window.print()} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm flex items-center gap-2"><Printer size={16} /> Print / PDF</button>}
          <button data-testid="export-report-button" onClick={doExport} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm flex items-center gap-2"><Download size={16} /> Export CSV</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 print:hidden">
        {visibleReports.map(([k, l]) => (
          <button key={k} data-testid={`report-tab-${k}`} onClick={() => setActive(k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${active === k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{l}</button>
        ))}
      </div>

      {active === "lead-journey" && (
        <div className="space-y-3" data-testid="lead-journey-report">
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="w-full min-w-[1600px] text-xs">
                <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-slate-50 z-20">Lead #</th>
                    <th className="text-left px-3 py-2 font-semibold">Customer</th>
                    <th className="text-left px-3 py-2 font-semibold">Product</th>
                    <th className="text-right px-3 py-2 font-semibold">Loan Amt</th>
                    <th className="text-left px-3 py-2 font-semibold">Lender</th>
                    <th className="text-left px-3 py-2 font-semibold">Partner</th>
                    <th className="text-left px-3 py-2 font-semibold">Sales Mgr</th>
                    <th className="text-left px-3 py-2 font-semibold">Ops Mgr</th>
                    <th className="text-left px-3 py-2 font-semibold">Current Stage</th>
                    <th className="text-right px-3 py-2 font-semibold">Pending (d)</th>
                    <th className="text-right px-3 py-2 font-semibold">Total (d)</th>
                    {(journey.stages || []).map((s) => (
                      <th key={s} colSpan={2} className="text-center px-3 py-2 font-semibold border-l border-slate-200 whitespace-nowrap">{s}</th>
                    ))}
                    <th className="text-right px-3 py-2 font-semibold border-l border-slate-200">Sanction</th>
                    <th className="text-right px-3 py-2 font-semibold">Disbursed</th>
                    <th className="text-right px-3 py-2 font-semibold">IRR</th>
                    <th className="text-right px-3 py-2 font-semibold">Tenure</th>
                    <th className="text-left px-3 py-2 font-semibold">PAN</th>
                    <th className="text-left px-3 py-2 font-semibold">City</th>
                    <th className="text-left px-3 py-2 font-semibold">Bank App No</th>
                    <th className="text-left px-3 py-2 font-semibold">Created</th>
                  </tr>
                  <tr className="text-[9px] text-slate-400 border-b border-slate-200 bg-slate-50">
                    <th colSpan={11} className="px-3 py-1 sticky left-0 bg-slate-50 z-20"></th>
                    {(journey.stages || []).map((s) => (
                      <th key={s} colSpan={2} className="px-3 py-1 border-l border-slate-200 whitespace-nowrap"><span className="pr-3">Entered</span><span>Days</span></th>
                    ))}
                    <th colSpan={7} className="border-l border-slate-200"></th>
                  </tr>
                </thead>
                <tbody>
                  {(journey.rows || []).map((r) => (
                    <tr key={r.lead_id} data-testid={`journey-row-${r.lead_id}`} className="border-b border-slate-100 hover:bg-slate-50/80 text-slate-700">
                      <td className="px-3 py-2 font-mono font-semibold text-emerald-700 sticky left-0 bg-white z-10">{r.lead_no || "—"}</td>
                      <td className="px-3 py-2"><div className="font-medium text-slate-900 whitespace-nowrap">{r.customer_name}</div><div className="text-[10px] text-slate-400">{r.customer_phone}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.loan_type}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatINR(r.loan_amount)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.lender_name || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.partner_name || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.sales_manager_name || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.ops_manager_name || r.final_pdd_ops_manager || "—"}</td>
                      <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 whitespace-nowrap">{r.current_status}</span></td>
                      <td className="px-3 py-2 text-right font-mono"><span className={r.pending_days_in_current_stage > 15 ? "text-rose-600 font-semibold" : "text-slate-700"}>{r.pending_days_in_current_stage ?? "—"}</span></td>
                      <td className="px-3 py-2 text-right font-mono">{r.total_days_since_created ?? "—"}</td>
                      {(journey.stages || []).map((s) => (
                        <td key={s} colSpan={2} className="px-3 py-2 border-l border-slate-100 whitespace-nowrap">
                          <span className="inline-block w-24 text-slate-500">{r.stage_entered?.[s] ? formatDate(r.stage_entered[s]) : "—"}</span>
                          <span className={`inline-block w-14 text-right font-mono ${r.stage_days?.[s] != null && r.stage_days[s] > 15 ? "text-amber-600 font-semibold" : "text-slate-500"}`}>{r.stage_days?.[s] != null ? `${r.stage_days[s]}d` : "—"}</span>
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right font-mono border-l border-slate-100">{formatINR(r.sanction_amount)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatINR(r.disbursed_amount)}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.irr || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.tenure || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.pan || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.city || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.bank_application_no || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{r.created_at ? formatDate(r.created_at) : "—"}</td>
                    </tr>
                  ))}
                  {(journey.rows || []).length === 0 && (
                    <tr><td colSpan={11 + (journey.stages || []).length * 2 + 7} className="px-4 py-12 text-center text-slate-400 text-sm">No leads to show. This report is scoped to leads you are associated with.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="text-[11px] text-slate-400">Journey scoped by role — admins see every lead; Sales Managers and DSAs see their downline; each row spans the full stage history with entry date and days-in-stage.</div>
        </div>
      )}

      {active === "custom-fields" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
            <div className="flex-1">
              <label className={labelCls}>Product</label>
              <select data-testid="cf-product-select" value={cfProduct} onChange={(e) => { setCfProduct(e.target.value); setCfKey(""); }} className={inputCls}>
                <option value="">Select product…</option>
                {products.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className={labelCls}>Custom Field</label>
              <select data-testid="cf-field-select" value={cfKey} onChange={(e) => setCfKey(e.target.value)} className={inputCls} disabled={!fieldOptions.length}>
                <option value="">{fieldOptions.length ? "Select field…" : "No custom fields"}</option>
                {fieldOptions.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className={labelCls}>Value contains (optional)</label>
              <input data-testid="cf-value-input" value={cfValue} onChange={(e) => setCfValue(e.target.value)} className={inputCls} placeholder="Filter by value" />
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead><tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">{fieldOptions.find((f) => f.key === cfKey)?.label || "Field Value"}</th>
                <th className="text-left px-4 py-3">Partner</th><th className="text-left px-4 py-3">Status</th>
              </tr></thead>
              <tbody>
                {cfRows.map((l, i) => (
                  <tr key={l.id} data-testid={`cf-row-${i}`} className="border-b border-slate-100 text-sm text-slate-700">
                    <td className="px-4 py-3 font-medium text-slate-900">{l.customer_name}</td>
                    <td className="px-4 py-3">{l.loan_type}</td>
                    <td className="px-4 py-3 font-medium text-emerald-700">{l.custom_fields?.[cfKey] ?? "—"}</td>
                    <td className="px-4 py-3">{l.partner_name || "—"}</td>
                    <td className="px-4 py-3">{l.status}</td>
                  </tr>
                ))}
                {(!cfKey || cfRows.length === 0) && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400 text-sm">{cfKey ? "No matching leads." : "Select a product and custom field to report."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {active === "payout-statement" && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm print:hidden flex flex-col sm:flex-row sm:items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px] max-w-md">
              <label className={labelCls}>Select Partner</label>
              <select data-testid="stmt-partner-select" value={stmtPartner} onChange={(e) => setStmtPartner(e.target.value)} className={inputCls}>
                <option value="">Select partner…</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.partner_type})</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Month</label>
              <input data-testid="stmt-month-input" type="month" value={stmtMonth} disabled={stmtAll} onChange={(e) => setStmtMonth(e.target.value)} className={`${inputCls} disabled:bg-slate-50`} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 pb-2 cursor-pointer">
              <input data-testid="stmt-alltime-toggle" type="checkbox" checked={stmtAll} onChange={(e) => setStmtAll(e.target.checked)} className="accent-emerald-600 w-4 h-4" /> All time
            </label>
          </div>
          {stmt && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="statement-view">
              <div className="flex items-start justify-between border-b border-slate-200 pb-4 mb-4">
                <div>
                  <div className="text-lg font-bold text-slate-900 font-heading">Settlement Statement</div>
                  <div className="text-sm text-slate-500">{stmt.partner?.name} · {stmt.partner?.phone} · {stmt.partner?.city}</div>
                  <div className="text-xs text-emerald-600 font-medium mt-0.5">Period: {stmtPeriod}</div>
                </div>
                <div className="text-right">
                  <LogoMark className="w-9 h-9 ml-auto" />
                  <div className="text-xs text-slate-400 mt-1">Arjun Fintech LLP</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200"><div className="text-xs text-slate-400 uppercase font-semibold">Total Payout</div><div className="text-lg font-bold text-slate-900 font-heading">{formatINR(totals.total_net)}</div><div className="text-[11px] text-slate-400">{totals.count} payout(s)</div></div>
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200"><div className="text-xs text-emerald-600 uppercase font-semibold">Paid / Settled</div><div className="text-lg font-bold text-emerald-700 font-heading">{formatINR(totals.paid)}</div><div className="text-[11px] text-emerald-600">{totals.settled_count} settled</div></div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200"><div className="text-xs text-amber-600 uppercase font-semibold">Pending</div><div className="text-lg font-bold text-amber-700 font-heading">{formatINR(totals.pending)}</div><div className="text-[11px] text-amber-600">{totals.count - totals.settled_count} pending</div></div>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2">Date</th><th className="text-left py-2">Customer</th><th className="text-left py-2">Lender</th><th className="text-right py-2">Disbursed</th><th className="text-right py-2">Net Payout</th><th className="text-right py-2">Paid</th><th className="text-left py-2">Status</th><th className="text-left py-2">Settled On</th><th className="text-left py-2">Settlement Note</th>
                </tr></thead>
                <tbody>
                  {stmt.rows.map((r, i) => (
                    <tr key={r.id} data-testid={`stmt-row-${i}`} className="border-b border-slate-100 text-slate-700">
                      <td className="py-2 text-slate-500">{r.date ? formatDate(r.date) : "—"}</td>
                      <td className="py-2">{r.customer_name}{r.manual && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100">manual</span>}</td>
                      <td className="py-2">{r.lender_name || "—"}</td>
                      <td className="py-2 text-right font-mono">{formatINR(r.disbursed_amount)}</td>
                      <td className="py-2 text-right font-mono font-semibold text-emerald-600">{formatINR(r.net_payout)}</td>
                      <td className="py-2 text-right font-mono">{r.status === "settled" ? formatINR(r.paid_amount ?? r.net_payout) : "—"}</td>
                      <td className="py-2 capitalize">{r.status}</td>
                      <td className="py-2 text-slate-500">{r.settled_at ? formatDate(r.settled_at) : "—"}</td>
                      <td className="py-2 text-slate-500">{r.settle_note || "—"}</td>
                    </tr>
                  ))}
                  {stmt.rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">No payouts for this partner in this period.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!special && c && (
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                {c.map(([, label]) => <th key={label} className="text-left px-4 py-3">{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} data-testid={`report-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/80 text-sm text-slate-700">
                  {c.map(([key, , fmt]) => <td key={key} className="px-4 py-3">{fmt ? fmt(row[key]) : (row[key] ?? "—")}</td>)}
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={c.length} className="px-4 py-12 text-center text-slate-400 text-sm">No data available.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
